<template>
  <teleport to="body">
    <transition name="fade">
      <div v-if="open" class="popup-container">
        <div
          class="popup inline-block align-bottom bg-white text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl w-full rounded-lg"
        >
          <h3 class="text-lg leading-6 font-medium text-gray-900">
            <div class="pb-3">Bulk import banned domains</div>
          </h3>

          <!-- Phase: pick -->
          <div v-if="phase === 'pick'">
            <p class="text-sm text-gray-500 mb-4">
              Upload a CSV with one domain per line. The first column is used; a
              header row named <code>domain</code> is optional. Emails are
              accepted and reduced to their domain. Max 5 MB.
            </p>
            <label
              for="banned-domains-file"
              class="block text-sm font-medium text-gray-700 mb-1"
            >
              CSV file
            </label>
            <input
              id="banned-domains-file"
              ref="fileInput"
              type="file"
              accept=".csv,text/csv"
              class="block w-full text-sm text-gray-700"
              @change="onFileChange"
            />
            <p v-if="file" class="text-sm text-gray-500 mt-2 break-all">
              <DocumentTextIcon class="h-5 inline mr-1 text-gray-400" />
              {{ file.name }} ({{ formatSize(file.size) }})
            </p>
            <p v-if="fileError" class="text-red-500 text-sm mt-2">
              {{ fileError }}
            </p>
            <div
              class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse rounded-b mt-4"
            >
              <button
                type="button"
                class="button-submit sm:ml-3"
                :disabled="!canSubmit || submitting"
                @click="submit"
              >
                Import
              </button>
              <button
                type="button"
                class="button-cancel ml-0 sm:ml-3"
                @click="close"
              >
                Cancel
              </button>
            </div>
          </div>

          <!-- Phase: running -->
          <div v-else-if="phase === 'running'">
            <div class="flex flex-col items-center py-6">
              <PulseLoader color="#ff5a5f" />
              <p class="text-sm text-gray-700 mt-4 break-all">
                Importing {{ filename }}…
              </p>
              <p class="text-xs text-gray-500 mt-1">{{ statusLabel }}</p>
            </div>
          </div>

          <!-- Phase: timeout -->
          <div v-else-if="phase === 'timeout'">
            <p class="text-sm text-gray-700 py-4">
              The import is still running. You can close this window; the list
              will update when it finishes.
            </p>
            <div
              class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse rounded-b mt-4"
            >
              <button
                type="button"
                class="button-submit sm:ml-3"
                @click="close"
              >
                Close
              </button>
            </div>
          </div>

          <!-- Phase: done -->
          <div v-else-if="phase === 'done'">
            <div v-if="importData.status === 'COMPLETED'">
              <p class="text-sm font-medium text-green-700 mb-3">
                Import finished.
              </p>
              <dl class="text-sm text-gray-700 grid grid-cols-2 gap-y-1">
                <dt>Rows read</dt>
                <dd class="text-right font-medium">
                  {{ importData.total_rows }}
                </dd>
                <dt>Added</dt>
                <dd class="text-right font-medium">
                  {{ importData.added_count }}
                </dd>
                <dt>Already banned (skipped)</dt>
                <dd class="text-right font-medium">
                  {{ importData.skipped_existing_count }}
                </dd>
                <dt>Invalid</dt>
                <dd class="text-right font-medium">
                  {{ importData.invalid_count }}
                </dd>
              </dl>
              <div v-if="invalidRows.length" class="mt-4">
                <p class="text-sm font-medium text-gray-700 mb-1">
                  Invalid rows
                </p>
                <ul
                  class="text-xs text-gray-600 border rounded max-h-40 overflow-y-auto divide-y"
                >
                  <li
                    v-for="(item, index) in invalidRows"
                    :key="index"
                    class="px-2 py-1 break-all"
                  >
                    Row {{ item.row }}: "{{ item.value }}" — {{ item.error }}
                  </li>
                </ul>
                <p
                  v-if="importData.invalid_count > invalidRows.length"
                  class="text-xs text-gray-500 mt-1"
                >
                  Showing the first {{ invalidRows.length }} of
                  {{ importData.invalid_count }} invalid rows.
                </p>
              </div>
            </div>
            <div v-else>
              <p class="text-sm font-medium text-red-600 mb-2">
                Import failed.
              </p>
              <p
                v-if="importData.error_message"
                class="text-sm text-gray-700 break-words"
              >
                {{ importData.error_message }}
              </p>
            </div>
            <div
              class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse rounded-b mt-4"
            >
              <button
                type="button"
                class="button-submit sm:ml-3"
                @click="close"
              >
                Close
              </button>
              <button
                type="button"
                class="button-cancel ml-0 sm:ml-3"
                @click="resetState"
              >
                Import another file
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { ref } from "vue";
import PulseLoader from "vue-spinner/src/PulseLoader";
import { DocumentTextIcon } from "@heroicons/vue/outline";
import eventBus from "@/utils/eventBus";
import formMixin from "@/mixins/formMixin";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const POLL_INTERVAL_MS = 2000;
// 5 minutes at one poll every 2 seconds, then the modal shows the timeout
// message but keeps polling in the background so the list still refreshes.
const MAX_POLLS = 150;
// Hard stop for background polling: 30 minutes at one poll every 2 seconds.
const MAX_BACKGROUND_POLLS = 900;

const STATUS_LABELS = {
  PENDING: "Queued",
  PROCESSING: "Processing",
};

export default {
  name: "BannedDomainsImportModal",
  components: {
    PulseLoader,
    DocumentTextIcon,
  },
  mixins: [formMixin],
  props: {
    open: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:open", "imported"],
  setup() {
    return {
      phase: ref("pick"),
      file: ref(null),
      fileError: ref(""),
      submitting: ref(false),
      importData: ref({}),
      pollTimer: ref(null),
      pollCount: ref(0),
      polling: ref(false),
      importedEmitted: ref(false),
    };
  },
  computed: {
    canSubmit() {
      return !!this.file && !this.fileError;
    },
    filename() {
      return this.importData?.original_filename || this.file?.name || "";
    },
    statusLabel() {
      return STATUS_LABELS[this.importData?.status] || "Queued";
    },
    invalidRows() {
      return this.importData?.invalid_rows || [];
    },
  },
  watch: {
    open(value) {
      // Closing the modal keeps an in-flight import polling so the parent
      // still gets `imported`; reopening then shows its progress.
      if (value && !this.pollTimer) {
        this.resetState();
      }
    },
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    resetState() {
      this.stopPolling();
      this.phase = "pick";
      this.file = null;
      this.fileError = "";
      this.submitting = false;
      this.importData = {};
      this.pollCount = 0;
      this.importedEmitted = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    close() {
      this.$emit("update:open", false);
    },
    stopPolling() {
      if (this.pollTimer) {
        window.clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      this.polling = false;
    },
    onFileChange(event) {
      const file = event.target.files?.[0] || null;
      this.file = file;
      this.fileError = this.validateFile(file);
    },
    validateFile(file) {
      if (!file) {
        return "";
      }
      const isCsv =
        file.name.toLowerCase().endsWith(".csv") || file.type === "text/csv";
      if (!isCsv) {
        return "Only .csv files are accepted.";
      }
      if (file.size > MAX_FILE_SIZE) {
        return "File size is too large (max 5 MB).";
      }
      if (file.size === 0) {
        return "The submitted file is empty.";
      }
      return "";
    },
    formatSize(bytes) {
      if (bytes < 1024) {
        return `${bytes} B`;
      }
      if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
      }
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    },
    async submit() {
      if (!this.canSubmit || this.submitting) {
        return;
      }
      this.submitting = true;
      const formData = new FormData();
      formData.append("file", this.file);
      try {
        const response = await this.handleSubmissionErrors(
          () => this.$alfredService.bannedDomainsImportStart(formData),
          { throwExceptions: true }
        );
        this.importData = response || {};
        this.phase = "running";
        this.startPolling();
      } catch (e) {
        const fileErrors = e?.response?.data?.file;
        if (Array.isArray(fileErrors) && fileErrors.length) {
          this.fileError = fileErrors[0];
        } else if (typeof fileErrors === "string") {
          this.fileError = fileErrors;
        }
      } finally {
        this.submitting = false;
      }
    },
    startPolling() {
      this.stopPolling();
      this.pollCount = 0;
      this.pollTimer = window.setInterval(this.poll, POLL_INTERVAL_MS);
    },
    async poll() {
      if (this.polling || !this.importData?.id) {
        return;
      }
      this.polling = true;
      this.pollCount += 1;
      try {
        const data = await this.$alfredService.bannedDomainsImportGet(
          this.importData.id
        );
        this.importData = data;
        if (data.status === "COMPLETED" || data.status === "ERROR") {
          this.stopPolling();
          this.finish();
          return;
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          this.stopPolling();
          this.importData = {
            ...this.importData,
            status: "ERROR",
            error_message: "The import could not be found.",
          };
          this.finish();
          return;
        }
        // Transient errors: keep polling until the cap is reached.
      } finally {
        this.polling = false;
      }
      if (this.pollCount >= MAX_BACKGROUND_POLLS) {
        this.stopPolling();
      } else if (this.pollCount >= MAX_POLLS && this.phase === "running") {
        this.phase = "timeout";
      }
    },
    finish() {
      this.phase = "done";
      if (this.importData.status === "COMPLETED" && !this.importedEmitted) {
        this.importedEmitted = true;
        eventBus.trigger("notify", {
          notification: {
            group: "top-right",
            text: "Banned domains imported.",
            type: "success",
          },
        });
        this.$emit("imported");
      }
    },
  },
};
</script>

<style>
/* The popup is teleported to <body>, so these rules cannot be scoped. */
.popup-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}
.popup {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
}
</style>
